import React, { useState, useEffect, useRef } from "react";
import "./MergeInvoicePopup.css";
import { formatForInput } from "../../../../Utils/datepickerFormat";
import {
  currencies,
  comboboxOption,
} from "../../../../Components/InvoiceEditor/constants"; 
import { Combobox } from "../../../../Components/InvoiceEditor/Components/CommonUI/index"; 
import { useSuppliers } from "../../../../Components/InvoiceEditor/supplierList"; 
import { useCategories } from "../../../../Components/InvoiceEditor/categoryList"; 
import { invoiceValidationSchema } from "../../../../Components/InvoiceEditor/InvoiceEditor.schema"; 
import { DatePickerNew } from "../../../../Components/InvoiceEditor/Components/CommonUI/DatePicker/DatePickerNew";

// ─── Types ───────────────────────────────────────────────────────────────────

interface MergeInvoiceItem {
  invoiceId: number;
  documentUrl?: string;
  type?: string;
  invoiceDate?: string;
  supplier?: string;
  supplierId?: number | null;
  documentReference?: string;
  dueDate?: string;
  category?: string;
  description?: string;
  currency?: string;
  totalAmount?: number;
  taxAmount?: number;
  [key: string]: any;
}

export interface MergeFormState {
  type: string;
  invoiceDate: string;
  supplier: string;
  supplierId: number | null;
  documentReference: string;
  dueDate: string;
  category: string;
  description: string;
  currency: string;
  totalAmount: string;
  taxAmount: string;
}

interface MergeInvoicePopupProps {
  items: MergeInvoiceItem[];
  loading?: boolean;
  onCancel: () => void;
  onMerge: (formData: MergeFormState) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const toDateInput = (raw?: string): string => {
  if (!raw) return "";
  return raw.split("T")[0]; // "2015-06-20T00:00:00" → "2015-06-20"
};

const toFormState = (item?: MergeInvoiceItem): MergeFormState => ({
  type: item?.type ?? "",
  invoiceDate: toDateInput(item?.invoiceDate),
  supplier: item?.supplier ?? "",
  supplierId: item?.supplierId ?? null,
  documentReference: item?.documentReference ?? "",
  dueDate: toDateInput(item?.dueDate),
  category: item?.category ?? "",
  description: item?.description ?? "",
  currency: item?.currency ?? "",
  totalAmount: item?.totalAmount != null ? String(item.totalAmount) : "",
  taxAmount: item?.taxAmount != null ? String(item.taxAmount) : "",
});

const buildPreviewUrls = (items: MergeInvoiceItem[]): string[] => {
  const urls: string[] = [];
  items.forEach((item) => {
    if (!item.documentUrl) return;
    item.documentUrl.split("$$$").filter(Boolean).forEach((url) => urls.push(url));
  });
  return urls;
};

const isPdfUrl = (url: string) => url.trim().toLowerCase().endsWith(".pdf");

// ─── Component ───────────────────────────────────────────────────────────────

const MergeInvoicePopup: React.FC<MergeInvoicePopupProps> = ({
  items,
  loading = false,
  onCancel,
  onMerge,
}) => {
  const [form, setForm] = useState<MergeFormState>(() => toFormState(items[0]));
  const [errors, setErrors] = useState<Partial<Record<keyof MergeFormState, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof MergeFormState, boolean>>>({});

  const supplierList: comboboxOption[] = useSuppliers();
  const categoryList: comboboxOption[] = useCategories();

  // Single shared zoom for the whole stack
  const [scale, setScale] = useState(1.0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const scrollStartRef = useRef({ left: 0, top: 0 });
  const scrollBoxRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    setForm(toFormState(items[0]));
    setErrors({});
    setTouched({});
  }, [items]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  const previewUrls = buildPreviewUrls(items);

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3.0));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));
  const resetZoom = () => setScale(1.0);

  const toDisplayDate = (iso: string): string => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return d && m && y ? `${d}/${m}/${y}` : "";
  };

  const fromDisplayDate = (display: string): string => {
    const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return display; 
    const [, d, m, y] = match;
    return `${y}-${m}-${d}`;
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale <= 1) return; 
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    scrollStartRef.current = {
      left: scrollBoxRef.current?.scrollLeft ?? 0,
      top: scrollBoxRef.current?.scrollTop ?? 0,
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollBoxRef.current) return;
    e.preventDefault();
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    scrollBoxRef.current.scrollLeft = scrollStartRef.current.left - dx;
    scrollBoxRef.current.scrollTop = scrollStartRef.current.top - dy;
  };

  const handleDateChange =
    (field: "invoiceDate" | "dueDate") => (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFieldChange(field, e.target.value);
    };

  const handleMouseUp = () => setIsDragging(false);

  const validateField = async (field: keyof MergeFormState, nextForm: MergeFormState) => {
    try {
      await invoiceValidationSchema.validateAt(field as string, nextForm);
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, [field]: err?.message }));
    }
  };

  const handleFieldChange = (field: keyof MergeFormState, value: string) => {
    const nextForm = { ...form, [field]: value };
    setForm(nextForm);
    validateField(field, nextForm);
  };

  const handleFieldBlur = (field: keyof MergeFormState) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, form);
  };

  const handleComboboxChange = (
    field: "supplier" | "category" | "currency",
    val: string,
    option: comboboxOption | null,
  ) => {
    const nextForm: MergeFormState = {
      ...form,
      [field]: val,
      ...(field === "supplier" ? { supplierId: option?.optionId ?? null } : {}),
    };
    setForm(nextForm);
    validateField(field, nextForm);
  };

  const hasError = (field: keyof MergeFormState) => !!(errors[field] && touched[field]);

  const handleMergeClick = () => {
    onMerge(form);
  };

  return (
    <div className="mip-overlay" role="dialog" aria-modal="true" aria-labelledby="mip-title">
      <div className="mip-backdrop" onClick={onCancel} />

      <div className="mip-modal">
        <div className="mip-header">
          <span className="mip-header-icon">⊞</span>
          <h2 className="mip-title" id="mip-title">Merge Items</h2>
          <button className="mip-close" onClick={onCancel} aria-label="Close">✕</button>
        </div>

        <div className="mip-summary">
          <span className="mip-summary-label">
            Merging {items.length} items:
          </span>
          {items.map((item) => (
            <span className="mip-chip" key={item.invoiceId ?? item.id}>
              #{item.invoiceId ?? item.id}
            </span>
          ))}
        </div>

        <div className="mip-body">
          <div className="mip-preview">
            <div className="mip-preview-toolbar">
              <button onClick={zoomOut} type="button">-</button>
              <span>{Math.round(scale * 100)}%</span>
              <button onClick={zoomIn} type="button">+</button>
              <button onClick={resetZoom} type="button">Reset</button>
            </div>

            <div
              className="mip-preview-scroll"
              ref={scrollBoxRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{
                cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
              }}
            >
              {previewUrls.length > 0 ? (
                previewUrls.map((url, idx) =>
                  isPdfUrl(url) ? (
                    <iframe
                      key={idx}
                      src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                      title={`document-${idx}`}
                      className="mip-preview-pdf"
                      style={{ width: `${scale * 100}%` }}
                    />
                  ) : (
                    <img
                      key={idx}
                      src={url}
                      alt={`document-${idx}`}
                      className="mip-preview-image"
                      style={{ width: `${scale * 100}%` }}
                      draggable={false}
                    />
                  ),
                )
              ) : (
                <div className="mip-preview-empty">No files to preview.</div>
              )}
            </div>
          </div>

          <div className="mip-form">
            <div className="mip-form-section-title">Item Details</div>

            <label className="mip-field">
              <span>Type</span>
              <select
                value={form.type}
                onChange={(e) => handleFieldChange("type", e.target.value)}
              >
                <option value="">Select type</option>
                <option value="Invoice">Invoice</option>
                <option value="Statement">Statement</option>
                <option value="Receipt">Receipt</option>
                <option value="Credit Note">Credit Note</option>
                <option value="Delivery Note">Delivery Note</option>
                <option value="Other">Other</option>
              </select>
            </label>

            <div className="mip-datepicker-field">
              <DatePickerNew
                disabled={false}
                inputType="text"
                name="invoiceDate"
                value={form.invoiceDate}
                label="Date"
                errorMessage={errors.invoiceDate}
                iserror={hasError("invoiceDate")}
                onChange={handleDateChange("invoiceDate")}
                onBlur={() => handleFieldBlur("invoiceDate")}
              />
            </div>
           
            <div className="mip-combobox-field">
              <Combobox
                name="supplier"
                label="Supplier"
                value={form.supplier}
                options={supplierList}
                errorMessage={errors.supplier}
                iserror={hasError("supplier")}
                onChange={(val: string, option: comboboxOption | null) =>
                  handleComboboxChange("supplier", val, option)
                }
                onBlur={() => handleFieldBlur("supplier")}
                allowAddNew
                addNewType="supplier"
              />
            </div>

            <label className="mip-field">
              <span>Document reference</span>
              <input
                type="text"
                value={form.documentReference}
                onChange={(e) => handleFieldChange("documentReference", e.target.value)}
                onBlur={() => handleFieldBlur("documentReference")}
              />
              {hasError("documentReference") && (
                <span className="mip-field-error">{errors.documentReference}</span>
              )}
            </label>

            <div className="mip-datepicker-field">
              <DatePickerNew
                disabled={false}
                inputType="text"
                name="dueDate"
                value={form.dueDate}
                label="Due Date"
                errorMessage={errors.dueDate}
                iserror={hasError("dueDate")}
                onChange={handleDateChange("dueDate")}
                onBlur={() => handleFieldBlur("dueDate")}
              />
            </div>

            <div className="mip-combobox-field">
              <Combobox
                name="category"
                label="Category"
                value={form.category}
                options={categoryList}
                errorMessage={errors.category}
                iserror={hasError("category")}
                onChange={(val: string, option: comboboxOption | null) =>
                  handleComboboxChange("category", val, option)
                }
                onBlur={() => handleFieldBlur("category")}
                allowAddNew
                addNewType="category"
              />
            </div>

            <label className="mip-field">
              <span>Description</span>
              <textarea
                value={form.description}
                onChange={(e) => handleFieldChange("description", e.target.value)}
                onBlur={() => handleFieldBlur("description")}
              />
              {hasError("description") && (
                <span className="mip-field-error">{errors.description}</span>
              )}
            </label>

            <div className="mip-form-section-title">Amount</div>

            <div className="mip-combobox-field">
              <Combobox
                name="currency"
                label="Currency"
                value={form.currency}
                options={currencies}
                errorMessage={errors.currency}
                iserror={hasError("currency")}
                onChange={(val: string, option: comboboxOption | null) =>
                  handleComboboxChange("currency", val, option)
                }
                onBlur={() => handleFieldBlur("currency")}
              />
            </div>
            <label className="mip-field">
              <span>Total amount</span>
              <input
                type="number"
                value={form.totalAmount}
                onChange={(e) => handleFieldChange("totalAmount", e.target.value)}
                onBlur={() => handleFieldBlur("totalAmount")}
              />
              {hasError("totalAmount") && (
                <span className="mip-field-error">{errors.totalAmount}</span>
              )}
            </label>

            <label className="mip-field">
              <span>Tax</span>
              <input
                type="number"
                value={form.taxAmount}
                onChange={(e) => handleFieldChange("taxAmount", e.target.value)}
                onBlur={() => handleFieldBlur("taxAmount")}
              />
              {hasError("taxAmount") && (
                <span className="mip-field-error">{errors.taxAmount}</span>
              )}
            </label>
          </div>
        </div>

        <div className="mip-footer">
          <button className="mip-btn mip-btn-cancel" onClick={onCancel}>Cancel</button>
          <button className="mip-btn mip-btn-merge" onClick={handleMergeClick}>{loading ? "Merging..." : "Merge"}</button>
        </div>
      </div>
    </div>
  );
};

export default MergeInvoicePopup;